import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmtINR, fmtUSD, fmtPct, fmtQty, colorByValue, isUSExchange } from "@/lib/format";
import { ArrowUpRight, ArrowDownRight, Plus, RefreshCw, Zap, Download, Split, Gift, Banknote, Sparkles, Share2, ChevronDown, FileText, FileLock, AlertTriangle, RotateCcw, Trophy, Medal, TrendingUp, TrendingDown, PieChart as PieChartIcon, Globe } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { usePortfolio } from "@/context/PortfolioContext";
import ShareDialog from "@/components/ShareDialog";
import Sparkline from "@/components/Sparkline";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";

const PALETTE = ["#2B8D7D", "#6FD3C4", "#A9E7DD", "#7E909F", "#4A5560", "#2B4A63", "#8A9099", "#0F4A42"];
const AUTO_REFRESH_MS = 2 * 60 * 1000; // 2 minutes for tighter freshness

// Market-cap badge styling — declared once at module scope so we don't recreate
// inline objects on every row render, and so the colour/label logic is read
// from a single lookup table instead of nested ternaries.
const MCAP_BADGE_STYLE = {
  LARGE: { background: "var(--tint-accent-bg)", color: "var(--tint-accent-fg)" },
  MID:   { background: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)" },
  SMALL: { background: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)" },
};
const MCAP_LABEL = { LARGE: "Large", MID: "Mid", SMALL: "Small" };

export default function Dashboard() {
  const { activeId, corpActions, applyCorpActions: ctxApplyCorpActions, refreshCorpActions } = usePortfolio();
  const [data, setData] = useState({ holdings: [], summary: null, prices: {}, prices_meta: {} });
  const [sparklines, setSparklines] = useState({});
  const [applyingCA, setApplyingCA] = useState(false);
  const [benchmarks, setBenchmarks] = useState({ benchmarks: [], since: null });
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingLive, setFetchingLive] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [lastLiveAt, setLastLiveAt] = useState(null);
  const [displayCcy, setDisplayCcy] = useState("INR");
  const [expandedSector, setExpandedSector] = useState(null);
  const [concentrationThreshold, setConcentrationThreshold] = useState(() => {
    const v = Number(localStorage.getItem("equitybox.concentration_threshold"));
    return [0, 40, 50, 60].includes(v) ? v : 60;
  });
  useEffect(() => {
    localStorage.setItem("equitybox.concentration_threshold", String(concentrationThreshold));
  }, [concentrationThreshold]);
  // What-If simulator
  const [whatifOptions, setWhatifOptions] = useState([]);
  const [whatifKey, setWhatifKey] = useState("NIFTYBEES");
  const [whatif, setWhatif] = useState(null);
  const [whatifLoading, setWhatifLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [lastLiveError, setLastLiveError] = useState(null);
  const timerRef = useRef(null);

  const downloadPDF = async (anonymized = false) => {
    setDownloadingPDF(true);
    try {
      const res = await api.get("/portfolio/report", {
        responseType: "blob",
        params: {
          ...(anonymized ? { anonymized: true } : {}),
          concentration_threshold: concentrationThreshold,
        },
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : (anonymized ? "EquityBox_client_report.pdf" : "EquityBox_report.pdf");
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(anonymized ? "Anonymized client report downloaded" : "Report downloaded");
    } catch (e) {
      toast.error("Could not generate report");
    } finally {
      setDownloadingPDF(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, t] = await Promise.all([
        api.get("/portfolio/summary"),
        api.get("/portfolio/benchmarks").catch(() => ({ data: { benchmarks: [], since: null } })),
        api.get("/transactions").catch(() => ({ data: [] })),
      ]);
      setData(s.data);
      setBenchmarks(b.data || { benchmarks: [], since: null });
      setTxns(t.data || []);
    } finally {
      setLoading(false);
    }
  };

  const fetchLive = async (silent = false) => {
    setFetchingLive(true);
    try {
      const r = await api.post("/prices/refresh", {});
      setLastLiveAt(new Date());
      setLastLiveError(null);
      const okN = r.data.updated?.length || 0;
      const errN = r.data.errors?.length || 0;
      // Track refresh quality so we can show a persistent warning when all
      // attempts fail (e.g. Yahoo IP-blocked in production).
      if (okN === 0 && errN > 0) {
        setLastLiveError({ all: true, count: errN });
      }
      if (!silent) {
        if (okN > 0 && errN === 0) toast.success(`Live prices refreshed for ${okN} holding${okN > 1 ? "s" : ""}`);
        else if (okN > 0 && errN > 0) {
          const failed = (r.data.errors || []).slice(0, 3).map(e => e.symbol).join(", ");
          const more = errN > 3 ? ` +${errN - 3} more` : "";
          toast.warning(`Updated ${okN} · ${errN} failed: ${failed}${more}`, { duration: 6000 });
        }
        else if (errN > 0) {
          const failed = (r.data.errors || []).slice(0, 3).map(e => `${e.symbol} (${e.error?.slice(0, 40)})`).join(", ");
          toast.error(`No prices fetched. ${failed}`, { duration: 6000 });
        }
      }
      await load();
    } catch (e) {
      setLastLiveError({ network: true, message: e?.response?.data?.detail || e?.message || "network error" });
      if (!silent) toast.error(e?.response?.data?.detail || "Live refresh failed");
    } finally {
      setFetchingLive(false);
    }
  };

  const loadSparklines = async () => {
    try {
      const r = await api.get("/portfolio/sparklines");
      setSparklines(r.data?.series || {});
    } catch (e) { /* sparklines are decorative; ignore failures */ }
  };

  const applyCorpActions = async (items) => {
    if (!items || items.length === 0) return;
    setApplyingCA(true);
    try {
      const res = await ctxApplyCorpActions(items);
      const n = res?.applied || 0;
      if (n > 0) toast.success(`Applied ${n} corporate action${n > 1 ? "s" : ""} to your ledger`);
      else toast.info("Nothing new to apply");
      await load();
      await Promise.all([loadSparklines(), loadWhatif(whatifKey)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply corporate actions");
    } finally {
      setApplyingCA(false);
    }
  };

  const loadWhatif = async (k) => {
    setWhatifLoading(true);
    try {
      const r = await api.get(`/portfolio/whatif?key=${encodeURIComponent(k)}`);
      setWhatif(r.data);
    } catch {
      setWhatif({ available: false, reason: "Failed to load" });
    } finally {
      setWhatifLoading(false);
    }
  };

  // Initial load + start interval; re-run on portfolio change.
  // Also refresh live prices whenever the user re-focuses the tab (handy
  // after switching to a broker tab to verify the live quote).
  useEffect(() => {
    if (!activeId) return;
    (async () => {
      await load();
      await fetchLive(true);
      loadSparklines();
      try {
        const r = await api.get("/portfolio/whatif/options");
        setWhatifOptions(r.data.options || []);
      } catch (e) { /* whatif options optional; ignore */ }
      loadWhatif(whatifKey);
    })();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => fetchLive(true), AUTO_REFRESH_MS);
    const onFocus = () => fetchLive(true);
    window.addEventListener("focus", onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const summary = data.summary || {};
  const holdings = data.holdings || [];
  const fx = Number(summary.fx_usd_inr) || 86;
  const fxChange = summary.fx_change_pct == null ? null : Number(summary.fx_change_pct);
  const usExposurePct = summary.us_exposure ? Number(summary.us_exposure.pct) : null;
  const hasUS = holdings.some((h) => h.currency === "USD");
  // Format an INR aggregate in the currently-selected display currency.
  const money = (inr, opts = {}) => (displayCcy === "INR" ? fmtINR(inr, opts) : fmtUSD((Number(inr) || 0) / fx, opts));
  // Format a holding's value that is stored in its native currency.
  const cmoney = (h, v, opts = {}) => (h.currency === "USD" ? fmtUSD(v, opts) : fmtINR(v, opts));

  const allocation = useMemo(() => holdings
    .filter(h => h.quantity > 0 && h.current_value > 0)
    .map((h, i) => ({ name: `${h.symbol}·${h.exchange}`, value: Number(h.current_value), color: PALETTE[i % PALETTE.length] })), [holdings]);

  const marketCapAllocation = useMemo(() => {
    const m = summary.market_cap_allocation || {};
    const labelMap = { LARGE: "Large Cap", MID: "Mid Cap", SMALL: "Small Cap" };
    const colorMap = { LARGE: "#2B8D7D", MID: "#6FD3C4", SMALL: "#A9E7DD" };
    return ["LARGE", "MID", "SMALL"]
      .map((k) => ({ key: k, name: labelMap[k], value: Number(m[k]?.value || 0), pct: Number(m[k]?.pct || 0), color: colorMap[k] }))
      .filter((r) => r.value > 0);
  }, [summary]);

  const sectorAllocation = useMemo(() => {
    const arr = summary.sector_allocation || [];
    return arr
      .filter((r) => Number(r.value) > 0)
      .map((r, i) => ({ name: r.sector, value: Number(r.value || 0), pct: Number(r.pct || 0), color: PALETTE[i % PALETTE.length] }));
  }, [summary]);

  const holdingsBySector = useMemo(() => {
    const map = {};
    (holdings || []).filter((h) => (h.quantity || 0) > 0).forEach((h) => {
      const sec = h.sector || "Other / Unclassified";
      const val = Number(h.current_value_inr ?? h.current_value ?? 0);
      if (!map[sec]) map[sec] = [];
      map[sec].push({ symbol: h.symbol, exchange: h.exchange, currency: h.currency, valueInr: val, value: Number(h.current_value ?? 0) });
    });
    Object.values(map).forEach((list) => list.sort((a, b) => b.valueInr - a.valueInr));
    return map;
  }, [holdings]);

  const concentratedSector = useMemo(() => {
    if (!concentrationThreshold || !sectorAllocation.length) return null;
    const top = sectorAllocation.reduce((a, b) => (b.pct > a.pct ? b : a), sectorAllocation[0]);
    return top.pct >= concentrationThreshold ? top : null;
  }, [sectorAllocation, concentrationThreshold]);

  const concentratedHolding = useMemo(() => {
    const cv = Number(summary.current_value || 0);
    if (!concentrationThreshold || cv <= 0) return null;
    let top = null;
    (holdings || []).filter((h) => (h.quantity || 0) > 0).forEach((h) => {
      const v = Number(h.current_value_inr ?? h.current_value ?? 0);
      const pct = (v / cv) * 100;
      if (!top || pct > top.pct) top = { symbol: h.symbol, exchange: h.exchange, pct };
    });
    return top && top.pct >= concentrationThreshold ? top : null;
  }, [holdings, summary, concentrationThreshold]);

  const returnsBreakdown = useMemo(() => ([
    { name: "Unrealized", value: Number(summary.unrealized_gain || 0) },
    { name: "Realized", value: Number(summary.realized_gain || 0) },
    { name: "Dividends", value: Number(summary.dividends || 0) },
  ]), [summary]);

  // ----- Recent corporate actions (last 90 days) — Split / Bonus / Dividend -----
  const recentActions = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    return (txns || [])
      .filter((t) => ["SPLIT", "BONUS", "DIVIDEND"].includes(t.txn_type))
      .filter((t) => {
        if (!t.date) return false;
        const d = new Date(t.date);
        return !isNaN(d) && d >= cutoff;
      })
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, 8);
  }, [txns]);

  const pendingCorpActions = useMemo(
    () => (corpActions.actions || []).filter((a) => !a.already_recorded),
    [corpActions]
  );

  const appliedAutoActions = useMemo(
    () => (txns || []).filter((t) => t.source === "corp_action_auto")
      .sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [txns]
  );

  const dividendStats = useMemo(
    () => computeDividendStats(txns, pendingCorpActions, holdings, summary, fx),
    [txns, pendingCorpActions, holdings, summary, fx]
  );

  const undoAction = async (txnId) => {
    try {
      await api.delete(`/transactions/${txnId}`);
      toast.success("Reverted — action removed from your ledger");
      await load();
      await Promise.all([loadSparklines(), loadWhatif(whatifKey), refreshCorpActions()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revert action");
    }
  };

  const undoAllActions = async () => {
    try {
      const r = await api.delete("/portfolio/corporate-actions/applied");
      const n = r.data?.reverted || 0;
      toast.success(`Reverted ${n} auto-applied action${n === 1 ? "" : "s"}`);
      await load();
      await Promise.all([loadSparklines(), loadWhatif(whatifKey), refreshCorpActions()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revert actions");
    }
  };

  return (
    <Layout
      subtitle={`Portfolio Overview · ${displayCcy}${lastLiveAt ? ` · live ${timeAgo(lastLiveAt)}` : ""}`}
      title="Your investments at a glance"
      actions={
        <>
          <button data-testid="share-portfolio-button" onClick={() => setShareOpen(true)} className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm px-3 md:px-4 py-2 rounded border transition-colors hover:bg-white/5" style={{ borderColor: "var(--border-soft)", color: "var(--text-secondary)" }}>
            <Share2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span className="hidden sm:inline">Share</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="download-pdf-button"
                disabled={downloadingPDF}
                className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm px-3 md:px-4 py-2 rounded border transition-colors hover:bg-white/5 disabled:opacity-60"
                style={{ borderColor: "var(--border-soft)", color: "var(--text-secondary)" }}
              >
                <Download className={`w-3.5 h-3.5 ${downloadingPDF ? "animate-pulse" : ""}`} strokeWidth={1.75} />
                <span className="hidden sm:inline">{downloadingPDF ? "Building…" : "PDF report"}</span>
                <span className="sm:hidden">{downloadingPDF ? "…" : "PDF"}</span>
                <ChevronDown className="w-3 h-3 opacity-60" strokeWidth={1.75} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl w-72">
              <DropdownMenuItem
                data-testid="pdf-full-option"
                onSelect={() => downloadPDF(false)}
                className="cursor-pointer rounded-lg py-2.5"
              >
                <FileText className="w-4 h-4 mr-2.5" strokeWidth={1.75} />
                <div className="flex-1 leading-tight">
                  <div className="text-sm font-medium">Full PDF report</div>
                  <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Includes symbols, qty &amp; cost basis</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="pdf-client-option"
                onSelect={() => downloadPDF(true)}
                className="cursor-pointer rounded-lg py-2.5"
              >
                <FileLock className="w-4 h-4 mr-2.5" strokeWidth={1.75} />
                <div className="flex-1 leading-tight">
                  <div className="text-sm font-medium">Client PDF (anonymized)</div>
                  <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Symbols replaced with &ldquo;Holding 01&hellip;&rdquo;</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid="live-refresh-button" onClick={() => fetchLive(false)} disabled={fetchingLive} className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm px-3 md:px-4 py-2 rounded transition-colors hover:opacity-90 disabled:opacity-60" style={{ background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" }}>
            <Zap className={`w-3.5 h-3.5 ${fetchingLive ? "animate-pulse" : ""}`} strokeWidth={1.75} />
            <span className="hidden sm:inline">{fetchingLive ? "Fetching…" : "Live prices"}</span>
            <span className="sm:hidden">{fetchingLive ? "…" : "Live"}</span>
          </button>
          <button data-testid="refresh-button" onClick={load} aria-label="Refresh" className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm px-3 md:px-4 py-2 rounded border" style={{ borderColor: "var(--btn-secondary-border)", color: "var(--text-secondary)" }}>
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <Link to="/transactions" data-testid="add-transaction-cta" className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm px-3 md:px-4 py-2 rounded" style={{ background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" }}>
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span className="hidden sm:inline">Add transaction</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </>
      }
    >
      {loading && holdings.length === 0 ? (
        <div className="surface-card p-10 text-center label-overline" data-testid="dashboard-loading">Loading portfolio…</div>
      ) : (
        <>
          <ShareDialog open={shareOpen} onOpenChange={setShareOpen} />
          {/* Currency toggle */}
          {hasUS && (
            <div className="flex items-center justify-end mb-4" data-testid="currency-toggle">
              <div className="inline-flex items-center rounded-full p-0.5" style={{ background: "var(--bg-base)", border: "1px solid var(--border-soft)" }}>
                {[["INR", "\u20b9 INR"], ["USD", "$ USD"]].map(([c, lbl]) => (
                  <button
                    key={c}
                    data-testid={`currency-toggle-${c.toLowerCase()}`}
                    onClick={() => setDisplayCcy(c)}
                    className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
                    style={displayCcy === c
                      ? { background: "var(--accent-primary)", color: "var(--btn-primary-fg)" }
                      : { color: "var(--text-muted)" }}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
              <span className="ml-3 text-[11px] font-mono" style={{ color: "var(--text-muted)" }} data-testid="fx-rate">1 USD = {fmtINR(fx)}</span>
              {fxChange != null && (
                <span
                  className="ml-2 inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full"
                  data-testid="fx-trend-badge"
                  style={{
                    background: fxChange >= 0 ? "var(--tint-accent-bg)" : "var(--tint-neg-bg, rgba(179,73,47,0.12))",
                    color: fxChange >= 0 ? "var(--accent-positive)" : "var(--accent-negative)",
                  }}
                >
                  {fxChange >= 0 ? <TrendingUp className="w-3 h-3" strokeWidth={2} /> : <TrendingDown className="w-3 h-3" strokeWidth={2} />}
                  {fmtPct(fxChange)} today
                </span>
              )}
              {usExposurePct != null && (
                <span
                  className="ml-2 inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full"
                  data-testid="us-exposure-chip"
                  title="Share of portfolio value held in US (USD) stocks"
                  style={{ background: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)" }}
                >
                  <Globe className="w-3 h-3" strokeWidth={2} />
                  US exposure {usExposurePct.toFixed(1)}%
                </span>
              )}
            </div>
          )}
          {hasUS && usExposurePct != null && (
            <div className="flex items-center justify-end mb-6" data-testid="geo-split">
              <div className="w-full max-w-sm">
                <div className="flex items-center justify-between text-[10px] font-mono mb-1.5" style={{ color: "var(--text-muted)" }}>
                  <span data-testid="geo-split-india">India {(100 - usExposurePct).toFixed(1)}%</span>
                  <span className="label-overline text-[9px]">Geographic mix</span>
                  <span data-testid="geo-split-us">US {usExposurePct.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 w-full rounded-sm overflow-hidden flex" title={`India ${(100 - usExposurePct).toFixed(1)}% · US ${usExposurePct.toFixed(1)}%`}>
                  <div className="transition-all" style={{ width: `${100 - usExposurePct}%`, background: "var(--accent-primary)" }} />
                  <div className="transition-all" style={{ width: `${usExposurePct}%`, background: "var(--tint-neutral-fg)" }} />
                </div>
              </div>
            </div>
          )}
          {/* Hero metrics */}
          <section className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-5 mb-6 md:mb-8" data-testid="summary-cards">
            <MetricCard
              testid="metric-invested"
              label="Initial Capital · Day 1"
              value={money(summary.initial_capital || summary.total_capital_deployed)}
              hint="Capital deployed on day-1 (first investment)"
            />
            <MetricCard
              testid="metric-currently-invested"
              label="Currently Invested"
              value={money(summary.total_invested_active)}
              hint="Cost basis of active positions"
            />
            <MetricCard
              testid="metric-current"
              label="Current Value"
              value={money(summary.current_value)}
              hint="Live market value · today"
            />
            <MetricCard
              testid="metric-day-gain"
              label="Day's Gain"
              value={money(summary.day_gain, { showSign: true })}
              valueClass={colorByValue(summary.day_gain)}
              hint={`vs. previous close · ${fmtPct(summary.day_gain_pct)}`}
              trend={Number(summary.day_gain) >= 0 ? "up" : "down"}
            />
            <MetricCard
              testid="metric-absolute-gain"
              label="Absolute Gain"
              value={money(summary.absolute_gain, { showSign: true })}
              valueClass={colorByValue(summary.absolute_gain)}
              hint="Unrealized + Realized + Dividends"
              trend={Number(summary.absolute_gain) >= 0 ? "up" : "down"}
            />
          </section>

          <StalePricesBanner
            oldestAt={summary.prices_oldest_at}
            lastError={lastLiveError}
            fetching={fetchingLive}
            onRetry={() => fetchLive(false)}
            holdingsCount={holdings.filter((h) => (h.quantity || 0) > 0).length}
          />

          {/* Sub metrics */}
          <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-6 mb-6 md:mb-8" data-testid="sub-metrics">
            <SubMetric testid="metric-net-deployed" label="Net Deployed" value={summary.total_capital_deployed} positiveOnly hint="Cumulative buys" money={money} />
            <SubMetric testid="metric-return-pct" label="Absolute Return" value={summary.absolute_return_pct} suffix="%" hint={summary.return_basis === "currently_invested" ? "On currently invested" : "On day-1 capital"} />
            <SubMetric testid="metric-xirr" label="XIRR" value={summary.xirr_pct} suffix="%" hint="Money-weighted, annualised" />
            <SubMetric testid="metric-unrealized" label="Unrealized Gain" value={summary.unrealized_gain} money={money} />
            <SubMetric testid="metric-realized" label="Realized Gain" value={summary.realized_gain} money={money} />
            <SubMetric testid="metric-dividends" label="Dividends" value={summary.dividends} positiveOnly money={money} />
          </section>

          {/* Automated corporate-actions engine — review & apply */}
          <CorporateActionsPanel
            pending={pendingCorpActions}
            counts={corpActions.counts || {}}
            applying={applyingCA}
            onApply={applyCorpActions}
          />

          {/* Auto-applied corporate actions — revert if they look off */}
          <AppliedActionsPanel actions={appliedAutoActions} onUndo={undoAction} onUndoAll={undoAllActions} />

          {/* Recent corporate actions (last 90 days) */}
          {recentActions.length > 0 && (
            <section className="surface-card p-6 md:p-7 mb-8" data-testid="recent-corporate-actions">
              <div className="flex items-end justify-between mb-4 gap-4">
                <div>
                  <p className="label-overline mb-1">Last 90 days</p>
                  <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em]">Recent corporate actions</h3>
                </div>
                <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                  {recentActions.length} event{recentActions.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
                {recentActions.map((a) => (
                  <CorporateActionTile key={a.id} action={a} />
                ))}
              </div>
            </section>
          )}

          {/* What-If simulator */}
          <section className="surface-card p-6 md:p-7 mb-8" data-testid="whatif-panel">
            <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
              <div>
                <p className="label-overline mb-1 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" strokeWidth={2} /> What-If simulator
                </p>
                <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em]">If every rupee had gone into…</h3>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  Invests your <b>Initial Capital · Day 1</b> into the chosen asset on day-1 and compares against your actual day-1 picks today.
                </p>
              </div>
              <Select
                value={whatifKey}
                onValueChange={(v) => { setWhatifKey(v); loadWhatif(v); }}
              >
                <SelectTrigger
                  data-testid="whatif-select"
                  className="rounded w-auto min-w-[220px] px-4 py-2 text-sm border"
                  style={{ borderColor: "var(--btn-secondary-border)", color: "var(--text-primary)", background: "var(--bg-surface)" }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {whatifOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {whatifLoading ? (
              <div className="py-8 text-center label-overline">Crunching scenario…</div>
            ) : !whatif || !whatif.available ? (
              <div className="py-6 text-sm" style={{ color: "var(--text-muted)" }}>
                {whatif?.reason || "No data available for this scenario yet."}
              </div>
            ) : (
              <WhatifPanel data={whatif} />
            )}
          </section>

          {/* Charts */}
          <section className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
            <div className="surface-card p-6 md:p-8 lg:col-span-3" data-testid="chart-allocation">
              <p className="label-overline mb-1">Allocation</p>
              <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em] mb-6">Where your capital lives</h3>
              {allocation.length === 0 ? (
                <EmptyChart text="No holdings yet" />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                  <div className="h-64">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={60} outerRadius={92} paddingAngle={2}>
                          {allocation.map((e) => <Cell key={e.name} fill={e.color} />)}
                        </Pie>
                        <Tooltip content={<ChartTooltip suffix="" formatter={fmtINR} />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="space-y-2.5">
                    {allocation.slice(0, 8).map((a) => {
                      const total = allocation.reduce((s, x) => s + x.value, 0) || 1;
                      const pct = (a.value / total) * 100;
                      return (
                        <li key={a.name} className="flex items-center justify-between text-sm">
                          <span className="inline-flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: a.color }} />
                            <span className="font-medium">{a.name}</span>
                          </span>
                          <span className="font-mono text-xs" style={{ color: "var(--text-secondary)" }}>{pct.toFixed(1)}%</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            <div className="surface-card p-6 md:p-8 lg:col-span-2" data-testid="chart-returns">
              <p className="label-overline mb-1">Returns mix</p>
              <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em] mb-6">Composition of gain</h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={returnsBreakdown} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="var(--border-soft)" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#7E909F" }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#7E909F" }} width={70} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTooltip formatter={fmtINR} />} cursor={{ fill: "rgba(126,144,159,0.08)" }} />
                    <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                      {returnsBreakdown.map((d) => (
                        <Cell key={d.name} fill={d.value >= 0 ? "#2B8D7D" : "var(--accent-negative)"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Market-Cap Allocation */}
          <section className="surface-card p-6 md:p-8 mb-8" data-testid="chart-marketcap">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="label-overline mb-1">Market-Cap Mix</p>
                <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em]">Large · Mid · Small Cap</h3>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>SEBI/AMFI classification · top 100 = Large, 101–250 = Mid, rest = Small</p>
              </div>
            </div>
            {marketCapAllocation.length === 0 ? (
              <EmptyChart text="No holdings to classify yet" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {marketCapAllocation.map((b) => (
                  <div key={b.key} className="p-4 rounded-xl" style={{ background: "var(--bg-base)", borderLeft: `4px solid ${b.color}` }} data-testid={`mcap-card-${b.key}`}>
                    <p className="label-overline" style={{ color: b.color }}>{b.name}</p>
                    <p className="font-metric text-2xl font-light mt-1.5">{fmtINR(b.value)}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{b.pct.toFixed(1)}% of portfolio</p>
                  </div>
                ))}
              </div>
            )}
            {marketCapAllocation.length > 0 && (
              <div className="h-1.5 w-full rounded-sm overflow-hidden flex" data-testid="mcap-bar">
                {marketCapAllocation.map((b) => (
                  <div
                    key={b.key}
                    title={`${b.name}: ${b.pct.toFixed(1)}%`}
                    style={{ width: `${b.pct}%`, background: b.color }}
                    className="transition-all"
                  />
                ))}
              </div>
            )}
          </section>

          {/* Sector Allocation */}
          <section className="surface-card p-6 md:p-8 mb-8" data-testid="chart-sectors">
            <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
              <div>
                <p className="label-overline mb-1 flex items-center gap-1.5">
                  <PieChartIcon className="w-3 h-3" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
                  Sector Mix
                </p>
                <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em]">Sector allocation</h3>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Where your capital is concentrated · by current value (₹) · click a sector to see its holdings</p>
              </div>
              <div className="flex items-center gap-2" data-testid="concentration-threshold">
                <span className="label-overline text-[10px]" style={{ color: "var(--text-muted)" }}>Alert at</span>
                <div className="inline-flex items-center rounded-full p-0.5" style={{ background: "var(--bg-base)", border: "1px solid var(--border-soft)" }}>
                  {[[40, "40%"], [50, "50%"], [60, "60%"], [0, "Off"]].map(([v, lbl]) => (
                    <button
                      key={v}
                      data-testid={`concentration-threshold-${v}`}
                      onClick={() => setConcentrationThreshold(v)}
                      className="px-2.5 py-1 rounded-full text-[11px] font-mono font-medium transition-colors"
                      style={concentrationThreshold === v
                        ? { background: "var(--accent-primary)", color: "var(--btn-primary-fg)" }
                        : { color: "var(--text-muted)" }}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {concentratedSector && (
              <div
                className="flex items-start gap-2.5 rounded px-4 py-3 mb-5"
                style={{ background: "var(--tint-neutral-bg)", border: "1px solid var(--border-soft)" }}
                data-testid="sector-concentration-alert"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
                <span className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                  <span className="font-medium" style={{ color: "var(--text-primary)" }}>{concentratedSector.name}</span>{" "}
                  makes up <span className="font-mono font-medium" style={{ color: "var(--accent-primary)" }}>{concentratedSector.pct.toFixed(1)}%</span>{" "}
                  of your portfolio. That's a heavy concentration — consider spreading across more sectors to reduce single-sector risk.
                </span>
              </div>
            )}
            {sectorAllocation.length === 0 ? (
              <EmptyChart text="No holdings to classify yet" />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="h-[240px]" data-testid="sector-pie">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sectorAllocation}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={58}
                        outerRadius={90}
                        paddingAngle={2}
                        onClick={(e) => setExpandedSector((prev) => (prev === e?.name ? null : e?.name))}
                      >
                        {sectorAllocation.map((e) => (
                          <Cell
                            key={e.name}
                            fill={e.color}
                            style={{ cursor: "pointer" }}
                            opacity={expandedSector && expandedSector !== e.name ? 0.4 : 1}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip suffix="" formatter={fmtINR} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-soft)" }} data-testid="sector-list">
                  {sectorAllocation.map((s) => {
                    const isOpen = expandedSector === s.name;
                    const members = holdingsBySector[s.name] || [];
                    return (
                      <div key={s.name} data-testid={`sector-row-${s.name.replace(/\W+/g, "-")}`}>
                        <button
                          type="button"
                          onClick={() => setExpandedSector(isOpen ? null : s.name)}
                          data-testid={`sector-toggle-${s.name.replace(/\W+/g, "-")}`}
                          className="w-full flex items-center justify-between gap-3 py-2.5 text-left transition-colors hover:bg-white/5 rounded"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={2} style={{ color: "var(--text-muted)" }} />
                            <span className="shrink-0 w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                            <span className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{s.name}</span>
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "var(--bg-base)", color: "var(--text-muted)" }}>{members.length}</span>
                          </div>
                          <div className="flex items-center gap-4 shrink-0">
                            <span className="font-mono text-sm">{s.pct.toFixed(1)}%</span>
                            <span className="font-mono text-sm w-28 text-right" style={{ color: "var(--text-muted)" }}>{fmtINR(s.value)}</span>
                          </div>
                        </button>
                        {isOpen && (
                          <div className="pl-8 pr-1 pb-3 flex flex-col gap-1.5" data-testid={`sector-holdings-${s.name.replace(/\W+/g, "-")}`}>
                            {members.map((m) => {
                              const wt = s.value > 0 ? (m.valueInr / s.value) * 100 : 0;
                              return (
                                <div key={`${m.symbol}:${m.exchange}`} className="flex items-center gap-3" data-testid={`sector-holding-${m.symbol}`}>
                                  <div className="flex items-center gap-2 min-w-0 w-40">
                                    <span className="text-[13px] font-medium truncate">{m.symbol}</span>
                                    <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{m.exchange}</span>
                                  </div>
                                  <div className="flex-1 h-1.5 rounded-sm overflow-hidden" style={{ background: "var(--border-soft)" }}>
                                    <div className="h-full transition-all" style={{ width: `${Math.max(2, wt)}%`, background: s.color }} />
                                  </div>
                                  <span className="font-mono text-[11px] w-12 text-right" style={{ color: "var(--text-secondary)" }}>{wt.toFixed(1)}%</span>
                                  <span className="font-mono text-[11px] w-24 text-right" style={{ color: "var(--text-muted)" }}>{fmtINR(m.valueInr)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* India vs US sleeve returns (mixed portfolios) */}
          {summary.sleeves?.mixed && (
            <section className="surface-card p-6 md:p-8 mb-8" data-testid="sleeve-returns">
              <div className="mb-5">
                <p className="label-overline mb-1 flex items-center gap-1.5">
                  <Globe className="w-3 h-3" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
                  India vs US
                </p>
                <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em]">Sleeve returns</h3>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  Each market's active holdings compared in ₹ — see which sleeve drives returns and income
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="label-overline" style={{ color: "var(--text-muted)" }}>
                      <th className="text-left py-2 pr-4 font-normal">Market</th>
                      <th className="text-right py-2 px-3 font-normal">Invested</th>
                      <th className="text-right py-2 px-3 font-normal">Value</th>
                      <th className="text-right py-2 px-3 font-normal">Unrealized</th>
                      <th className="text-right py-2 px-3 font-normal">Return</th>
                      <th className="text-right py-2 pl-3 font-normal">Dividends</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {[
                      { key: "india", label: "India (home)", dot: "var(--accent-primary)", s: summary.sleeves.india },
                      { key: "us", label: "Overseas (US)", dot: "var(--tint-neutral-fg)", s: summary.sleeves.us },
                    ].map(({ key, label, dot, s }) => (
                      <tr key={key} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`sleeve-row-${key}`}>
                        <td className="text-left py-3 pr-4">
                          <span className="inline-flex items-center gap-2 font-sans">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: dot }} />
                            {label}
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "var(--bg-base)", color: "var(--text-muted)" }}>{s.count}</span>
                          </span>
                        </td>
                        <td className="text-right py-3 px-3">{money(s.invested)}</td>
                        <td className="text-right py-3 px-3">{money(s.current_value)}</td>
                        <td className={`text-right py-3 px-3 ${colorByValue(s.unrealized)}`}>{money(s.unrealized, { showSign: true })}</td>
                        <td className={`text-right py-3 px-3 font-semibold ${colorByValue(s.return_pct)}`} data-testid={`sleeve-return-${key}`}>{fmtPct(s.return_pct)}</td>
                        <td className="text-right py-3 pl-3" style={{ color: "var(--accent-positive)" }}>{money(s.dividends)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs mt-4" style={{ color: "var(--text-muted)" }} data-testid="sleeve-leader-note">
                Your <span className="font-semibold" style={{ color: "var(--text-primary)" }}>
                  {summary.sleeves.us.return_pct >= summary.sleeves.india.return_pct ? "US" : "India"}
                </span> sleeve is the stronger performer
                ({fmtPct(Math.max(summary.sleeves.us.return_pct, summary.sleeves.india.return_pct))} vs {fmtPct(Math.min(summary.sleeves.us.return_pct, summary.sleeves.india.return_pct))}).
              </p>
            </section>
          )}

          {/* Benchmark comparison */}
          <section className="surface-card p-6 md:p-8 mb-8" data-testid="chart-benchmarks">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <div>
                <p className="label-overline mb-1">Benchmarks</p>
                <h3 className="font-display text-[21px] font-semibold tracking-[-0.015em]" data-testid="benchmark-heading">
                  Portfolio vs {benchmarks.us_benchmarks ? "US Indices" : "Indices"}
                </h3>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {benchmarks.us_benchmarks ? "vs US market indices" : "vs Indian market indices"} · Since {benchmarks.since || "—"} · since-inception return · CAGR annualised
                </p>
              </div>
              <div className="flex items-center gap-3" data-testid="portfolio-perf-badge">
                <div className="px-4 py-2 rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--bg-surface)" }}>
                  <p className="label-overline">Your Return</p>
                  <p className="font-metric text-[22px]">{fmtPct(summary.absolute_return_pct)}</p>
                </div>
                {summary.xirr_pct !== null && summary.xirr_pct !== undefined && (
                  <div className="px-4 py-2 rounded-lg border" style={{ borderColor: "var(--border-soft)", color: "var(--text-primary)" }}>
                    <p className="label-overline">XIRR</p>
                    <p className="font-metric text-[22px]">{fmtPct(summary.xirr_pct)}</p>
                  </div>
                )}
              </div>
            </div>
            {(!benchmarks.benchmarks || benchmarks.benchmarks.length === 0) ? (
              <p className="label-overline">No benchmark data yet — add a buy to compare against indices.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {benchmarks.benchmarks.map((b) => {
                  const ok = !b.error;
                  const delta = ok && summary.absolute_return_pct != null ? summary.absolute_return_pct - b.return_pct : null;
                  return (
                    <div key={b.key} className="p-4 rounded-xl" style={{ background: "var(--bg-base)" }} data-testid={`bench-${b.key}`}>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">{b.name}</p>
                        {ok && (
                          <span className={`text-sm font-mono ${colorByValue(b.return_pct)}`} data-testid={`bench-return-${b.key}`}>{fmtPct(b.return_pct)}</span>
                        )}
                      </div>
                      {ok ? (
                        <>
                          <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
                            since-inception · CAGR {fmtPct(b.cagr_pct)} · {b.days}d
                          </p>
                          {delta !== null && (
                            <p className={`text-xs mt-1.5 font-medium ${colorByValue(delta)}`} data-testid={`bench-delta-${b.key}`}>
                              You: {delta >= 0 ? "+" : ""}{delta.toFixed(2)}% {delta >= 0 ? "ahead" : "behind"}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Data unavailable</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Holdings */}
          <section className="surface-card overflow-hidden" data-testid="holdings-table">
            <div className="p-5 md:p-8 flex items-center justify-between">
              <div>
                <p className="label-overline mb-1">Holdings</p>
                <h3 className="font-display text-lg md:text-[21px] font-semibold tracking-[-0.015em]">Active positions</h3>
              </div>
              <Link to="/prices" className="text-xs md:text-sm" style={{ color: "var(--accent-primary)" }} data-testid="link-update-prices">Update prices →</Link>
            </div>
            {concentratedHolding && (
              <div
                className="mx-5 md:mx-8 mb-4 flex items-start gap-2.5 rounded px-4 py-3"
                style={{ background: "var(--tint-neutral-bg)", border: "1px solid var(--border-soft)" }}
                data-testid="holding-concentration-alert"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
                <span className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                  <span className="font-medium" style={{ color: "var(--text-primary)" }}>{concentratedHolding.symbol}</span>{" "}
                  alone is <span className="font-mono font-medium" style={{ color: "var(--accent-primary)" }}>{concentratedHolding.pct.toFixed(1)}%</span>{" "}
                  of your portfolio — a large single-stock position. Consider trimming or adding others to reduce stock-specific risk.
                </span>
              </div>
            )}
            {(() => {
              // Active = still hold positive quantity. Closed positions (sold-out)
              // are excluded from this table even though they may still contribute
              // realized gains / dividends in the totals above.
              const activeHoldings = holdings.filter((h) => (h.quantity || 0) > 0);
              if (activeHoldings.length === 0) {
                return (
                  <div className="px-8 pb-10 text-center" style={{ color: "var(--text-muted)" }}>
                    No active holdings.
                    {" "}
                    <Link to="/transactions" className="underline" style={{ color: "var(--accent-primary)" }}>Add your next BUY transaction</Link>
                    {" "}
                    to start a new position.
                  </div>
                );
              }
              return (
              <>
                {/* Mobile cards (< md) */}
                <div className="md:hidden flex flex-col divide-y" style={{ borderColor: "var(--border-soft)" }}>
                  {activeHoldings.map((h) => (
                    <HoldingCard key={`${h.symbol}:${h.exchange}-card`} h={h} spark={sparklines[`${h.symbol}:${h.exchange}`]} />
                  ))}
                </div>
                {/* Desktop table (>= md) */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead style={{ background: "var(--bg-base)" }}>
                      <tr>
                        <Th>Symbol</Th>
                        <Th right>Qty</Th>
                        <Th right>Avg cost</Th>
                        <Th right>Current</Th>
                        <Th center>Trend</Th>
                        <Th right>Day&apos;s gain</Th>
                        <Th right>Invested</Th>
                        <Th right>Value</Th>
                        <Th right>Unrealized</Th>
                        <Th right>Dividends</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeHoldings.map((h) => (
                        <tr key={`${h.symbol}:${h.exchange}`} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`holding-row-${h.symbol}`}>
                          <td className="py-4 px-6">
                            <div className="font-medium flex items-center gap-2">
                              {h.symbol}
                              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: h.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)", color: h.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)" }}>
                                {h.exchange || "NSE"}
                              </span>
                              {h.market_cap && (
                                <span
                                  className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded"
                                  style={MCAP_BADGE_STYLE[h.market_cap] || MCAP_BADGE_STYLE.SMALL}
                                  data-testid={`mcap-${h.symbol}`}
                                >
                                  {MCAP_LABEL[h.market_cap] || "Small"}
                                </span>
                              )}
                            </div>
                            {h.company_name && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{h.company_name}</div>}
                          </td>
                          <Td right mono>{fmtQty(h.quantity)}</Td>
                          <Td right mono>{cmoney(h, h.avg_cost, { decimals: 2 })}</Td>
                          <Td right mono>
                            {h.current_price > 0 ? cmoney(h, h.current_price) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                            {h.currency === "USD" && h.current_price_inr != null && (
                              <div className="text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>{fmtINR(h.current_price_inr)}</div>
                            )}
                          </Td>
                          <td className="py-4 px-6 text-center">
                            <div className="inline-flex justify-center" data-testid={`sparkline-${h.symbol}`}>
                              <Sparkline data={sparklines[`${h.symbol}:${h.exchange}`]} />
                            </div>
                          </td>
                          <Td right mono className={colorByValue(h.day_gain)}>
                            {h.previous_close ? (
                              <>
                                <div>{cmoney(h, h.day_gain, { showSign: true })}</div>
                                <div className="text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>{fmtPct(h.day_gain_pct)}</div>
                              </>
                            ) : (
                              <span style={{ color: "var(--text-muted)" }}>—</span>
                            )}
                          </Td>
                          <Td right mono>{cmoney(h, h.invested)}</Td>
                          <Td right mono>
                            {cmoney(h, h.current_value)}
                            {h.currency === "USD" && h.current_value_inr != null && (
                              <div className="text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>{fmtINR(h.current_value_inr)}</div>
                            )}
                          </Td>
                          <Td right mono className={colorByValue(h.unrealized_gain)}>
                            {cmoney(h, h.unrealized_gain, { showSign: true })}
                            <div className="text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>{fmtPct(h.unrealized_pct)}</div>
                            {h.currency === "USD" && h.unrealized_gain_inr != null && (
                              <div className="text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>{fmtINR(h.unrealized_gain_inr, { showSign: true })}</div>
                            )}
                          </Td>
                          <Td right mono>{cmoney(h, h.dividends)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
              );
            })()}
          </section>

          {/* Dividend Calendar & Income */}
          <DividendCalendar stats={dividendStats} />

          {/* Yield Leaderboard */}
          <YieldLeaderboard rows={dividendStats.rows} />
        </>
      )}
    </Layout>
  );
}

function AppliedActionsPanel({ actions, onUndo, onUndoAll }) {
  const [undoing, setUndoing] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [undoingAll, setUndoingAll] = React.useState(false);
  if (!actions || actions.length === 0) return null;

  const handleUndo = async (id) => {
    setUndoing(id);
    try { await onUndo(id); } finally { setUndoing(null); }
  };

  const handleUndoAll = async () => {
    setUndoingAll(true);
    try { await onUndoAll(); } finally { setUndoingAll(false); }
  };

  return (
    <section className="surface-card p-5 md:p-7 mb-6 md:mb-8" data-testid="applied-actions-panel">
      <div className="flex items-center justify-between gap-3">
        <button
          className="flex items-center gap-3 text-left min-w-0"
          onClick={() => setOpen((o) => !o)}
          data-testid="applied-actions-toggle"
        >
          <div className="min-w-0">
            <p className="label-overline mb-1 flex items-center gap-1.5">
              <RotateCcw className="w-3 h-3" strokeWidth={1.75} style={{ color: "var(--text-muted)" }} />
              Auto-Applied Actions
            </p>
            <h3 className="font-display text-base md:text-lg font-semibold tracking-[-0.015em]">
              {actions.length} action{actions.length > 1 ? "s" : ""} recorded automatically
            </h3>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.75} style={{ color: "var(--text-muted)" }} />
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              data-testid="revert-all-btn"
              disabled={undoingAll}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border transition-colors hover:bg-white/5 disabled:opacity-60 shrink-0"
              style={{ borderColor: "var(--accent-negative)", color: "var(--accent-negative)" }}
            >
              {undoingAll ? <RefreshCw className="w-3 h-3 animate-spin" strokeWidth={2} /> : <RotateCcw className="w-3 h-3" strokeWidth={2} />}
              Revert all
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="revert-all-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Revert all auto-applied actions?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes all {actions.length} corporate action{actions.length > 1 ? "s" : ""} the engine added to your ledger
                (dividends &amp; splits). Your quantities, average cost and passive income will recompute, and these
                actions will reappear as detected so you can re-apply them anytime. Manually-entered transactions are untouched.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="revert-all-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="revert-all-confirm" onClick={handleUndoAll}>Revert all</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {open && (
        <div className="flex flex-col divide-y mt-4" style={{ borderColor: "var(--border-soft)" }}>
          {actions.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`applied-action-${t.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className="shrink-0 w-7 h-7 rounded flex items-center justify-center"
                  style={{ background: t.txn_type === "DIVIDEND" ? "var(--tint-accent-bg)" : "var(--tint-neutral-bg)", color: t.txn_type === "DIVIDEND" ? "var(--tint-accent-fg)" : "var(--tint-neutral-fg)" }}
                >
                  {t.txn_type === "DIVIDEND" ? <Banknote className="w-4 h-4" strokeWidth={1.75} /> : <Split className="w-4 h-4" strokeWidth={1.75} />}
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">
                    {t.symbol}
                    <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                      {t.txn_type === "DIVIDEND" ? "Dividend" : "Split / Bonus"}
                    </span>
                  </div>
                  <div className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>
                    {t.date} · {t.txn_type === "DIVIDEND" ? (isUSExchange(t.exchange) ? fmtUSD(t.amount) : fmtINR(t.amount)) : `×${t.ratio}`}
                  </div>
                </div>
              </div>
              <button
                data-testid={`undo-action-${t.id}`}
                onClick={() => handleUndo(t.id)}
                disabled={undoing === t.id}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border transition-colors hover:bg-white/5 disabled:opacity-60"
                style={{ borderColor: "var(--btn-secondary-border)", color: "var(--text-secondary)" }}
              >
                {undoing === t.id ? <RefreshCw className="w-3 h-3 animate-spin" strokeWidth={2} /> : <RotateCcw className="w-3 h-3" strokeWidth={2} />}
                Undo
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function YieldLeaderboard({ rows }) {
  const earners = useMemo(() => (rows || []).filter((r) => r.total > 0).sort((a, b) => b.total - a.total).slice(0, 5), [rows]);
  const yoc = useMemo(() => (rows || []).filter((r) => r.yieldOnCost != null && r.ttm > 0).sort((a, b) => b.yieldOnCost - a.yieldOnCost).slice(0, 5), [rows]);
  if ((!earners || earners.length === 0) && (!yoc || yoc.length === 0)) return null;

  const rankColor = (i) => (i === 0 ? "var(--accent-primary)" : "var(--text-muted)");

  return (
    <section className="surface-card p-5 md:p-8 mt-6 md:mt-8" data-testid="yield-leaderboard">
      <div className="mb-5 md:mb-6">
        <p className="label-overline mb-1 flex items-center gap-1.5">
          <Trophy className="w-3 h-3" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
          Yield Leaderboard
        </p>
        <h3 className="font-display text-lg md:text-[21px] font-semibold tracking-[-0.015em]">Your income champions</h3>
        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Top dividend earners and best yield-on-cost picks</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-10">
        <div data-testid="leaderboard-earners">
          <p className="label-overline text-[9px] mb-3" style={{ color: "var(--text-muted)" }}>Top Earners · All-time</p>
          <div className="flex flex-col gap-2.5">
            {earners.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>No dividends yet.</p>
            ) : earners.map((r, i) => (
              <div key={`${r.symbol}:${r.exchange}`} className="flex items-center justify-between gap-3" data-testid={`leaderboard-earner-${r.symbol}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {i === 0
                    ? <Medal className="w-5 h-5 shrink-0" strokeWidth={2} style={{ color: "var(--accent-primary)" }} data-testid="leaderboard-earner-trophy" />
                    : <span className="font-mono text-sm w-5 shrink-0 text-center" style={{ color: rankColor(i) }}>{i + 1}</span>}
                  <span className="font-medium text-sm truncate">{r.symbol}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="w-20 md:w-28 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--border-soft)" }}>
                    <div className="h-full rounded-full" style={{ width: `${(r.total / earners[0].total) * 100}%`, background: "var(--accent-primary)" }} />
                  </div>
                  <span className="font-mono text-sm text-positive w-24 text-right">{fmtINR(r.total)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="leaderboard-yoc">
          <p className="label-overline text-[9px] mb-3" style={{ color: "var(--text-muted)" }}>Best Yield on Cost · TTM</p>
          <div className="flex flex-col gap-2.5">
            {yoc.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>No trailing-12-month dividends yet.</p>
            ) : yoc.map((r, i) => (
              <div key={`${r.symbol}:${r.exchange}`} className="flex items-center justify-between gap-3" data-testid={`leaderboard-yoc-${r.symbol}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {i === 0
                    ? <Trophy className="w-5 h-5 shrink-0" strokeWidth={2} style={{ color: "var(--accent-primary)" }} data-testid="leaderboard-yoc-trophy" />
                    : <span className="font-mono text-sm w-5 shrink-0 text-center" style={{ color: rankColor(i) }}>{i + 1}</span>}
                  <span className="font-medium text-sm truncate">{r.symbol}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>{fmtINR(r.ttm)} TTM</span>
                  <span className="font-mono text-sm w-16 text-right" style={{ color: "var(--accent-primary)" }}>{r.yieldOnCost.toFixed(2)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function computeDividendStats(txns, pending, holdings, summary, fx = 1) {
  const isUSD = (ex) => isUSExchange(ex);
  const toINR = (amt, ex) => Number(amt || 0) * (isUSD(ex) ? fx : 1);
  const now = new Date();
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString("en-US", { month: "short" }),
      yr: `'${String(d.getFullYear()).slice(2)}`,
      showYear: d.getMonth() === 0 || i === 11,
    });
  }
  const mMap = Object.fromEntries(months.map((m) => [m.key, 0]));
  const ttmCut = new Date(now); ttmCut.setDate(ttmCut.getDate() - 365);
  const ttmCutStr = ttmCut.toISOString().slice(0, 10);

  const holdingByKey = Object.fromEntries((holdings || []).map((h) => [`${h.symbol}:${h.exchange}`, h]));
  const perStock = {};
  (txns || []).filter((t) => t.txn_type === "DIVIDEND").forEach((t) => {
    // Normalise every dividend to ₹ so USD + INR payouts aggregate correctly.
    const amt = toINR(t.amount, t.exchange);
    const mk = (t.date || "").slice(0, 7);
    if (mk in mMap) mMap[mk] += amt;
    const k = `${t.symbol}:${t.exchange}`;
    if (!perStock[k]) perStock[k] = { symbol: t.symbol, exchange: t.exchange, company: t.company_name, total: 0, ttm: 0, count: 0, last: "" };
    const s = perStock[k];
    s.total += amt;
    if ((t.date || "") >= ttmCutStr) s.ttm += amt;
    s.count += 1;
    if (t.company_name) s.company = t.company_name;
    if ((t.date || "") > s.last) s.last = t.date || "";
  });

  const bars = months.map((m) => ({ name: m.label, yr: m.showYear ? m.yr : "", month: m.key, value: Math.round(mMap[m.key] * 100) / 100 }));
  const rows = Object.values(perStock).map((r) => {
    const h = holdingByKey[`${r.symbol}:${r.exchange}`] || {};
    // Use ₹-normalised holding values so yields (₹ dividends ÷ value) are consistent.
    const cv = Number(h.current_value_inr ?? h.current_value ?? 0);
    const inv = Number(h.invested_inr ?? h.invested ?? 0);
    return { ...r, ttmYield: cv > 0 ? (r.ttm / cv) * 100 : null, yieldOnCost: inv > 0 ? (r.ttm / inv) * 100 : null };
  }).sort((a, b) => b.total - a.total);

  const portTtm = rows.reduce((s, r) => s + r.ttm, 0);
  const cvTotal = Number(summary?.current_value || 0);
  const invTotal = Number(summary?.total_invested_active || 0);
  const awaitingItems = (pending || []).filter((a) => a.txn_type === "DIVIDEND");
  return {
    bars, rows,
    awaiting: awaitingItems,
    awaitingTotal: awaitingItems.reduce((s, a) => s + toINR(a.amount, a.exchange), 0),
    portTtm,
    portTtmYield: cvTotal > 0 ? (portTtm / cvTotal) * 100 : null,
    portYoC: invTotal > 0 ? (portTtm / invTotal) * 100 : null,
    totalPassive: Number(summary?.dividends || 0),
  };
}

function DividendCalendar({ stats }) {
  const { bars, rows, awaiting, awaitingTotal, portTtmYield, portYoC, totalPassive } = stats;
  const maxBar = Math.max(1, ...bars.map((b) => b.value));

  return (
    <section className="surface-card overflow-hidden mt-6 md:mt-8" data-testid="dividend-calendar">
      <div className="p-5 md:p-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5 md:mb-6">
          <div>
            <p className="label-overline mb-1">Dividend Calendar</p>
            <h3 className="font-display text-lg md:text-[21px] font-semibold tracking-[-0.015em]">Passive income</h3>
            <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Monthly cash dividends · trailing-12-month yields</p>
          </div>
          <div className="grid grid-cols-3 gap-4 md:gap-8 shrink-0">
            <div>
              <p className="label-overline text-[9px] mb-1">Passive Income · All-time</p>
              <p className="font-metric text-lg md:text-2xl font-light" style={{ color: "var(--accent-positive)" }} data-testid="total-passive-income">{fmtINR(totalPassive)}</p>
            </div>
            <div>
              <p className="label-overline text-[9px] mb-1">TTM Yield</p>
              <p className="font-metric text-lg md:text-2xl font-light" data-testid="ttm-yield">{portTtmYield == null ? "—" : `${portTtmYield.toFixed(2)}%`}</p>
            </div>
            <div>
              <p className="label-overline text-[9px] mb-1">Yield on Cost</p>
              <p className="font-metric text-lg md:text-2xl font-light" data-testid="yield-on-cost">{portYoC == null ? "—" : `${portYoC.toFixed(2)}%`}</p>
            </div>
          </div>
        </div>

        {awaiting.length > 0 && (
          <div
            className="flex items-center justify-between gap-3 rounded px-4 py-3 mb-5"
            style={{ background: "var(--tint-accent-bg)", border: "1px solid var(--border-soft)" }}
            data-testid="dividend-awaiting"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-4 h-4 shrink-0" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
              <span className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                <span className="font-medium" style={{ color: "var(--accent-primary)" }}>{fmtINR(awaitingTotal)}</span>
                {" "}across {awaiting.length} detected dividend{awaiting.length > 1 ? "s" : ""} awaiting — apply in Corporate Actions above to record.
              </span>
            </div>
          </div>
        )}

        {/* 12-month bar view (CSS grid bars — crisp on the dark theme) */}
        <div className="flex items-end gap-1.5 md:gap-2 h-36 md:h-44" data-testid="dividend-calendar-bar">
          {bars.map((b) => (
            <div key={b.month} className="flex-1 flex flex-col items-center justify-end h-full group">
              <div className="w-full flex items-end justify-center h-full">
                <div
                  className="w-full max-w-[36px] rounded-t transition-all duration-200"
                  style={{
                    height: `${b.value > 0 ? Math.max(4, (b.value / maxBar) * 100) : 0}%`,
                    background: b.value > 0 ? "var(--accent-primary)" : "var(--border-soft)",
                    minHeight: b.value > 0 ? 4 : 2,
                  }}
                  title={`${b.name}: ${fmtINR(b.value)}`}
                />
              </div>
              <span className="label-overline text-[9px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                {b.name}{b.yr && <span className="ml-0.5" style={{ opacity: 0.7 }}>{b.yr}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-8 pb-10 text-center" style={{ color: "var(--text-muted)" }}>
          No dividends recorded yet. Detect them with <span style={{ color: "var(--accent-primary)" }}>Corporate Actions</span> above.
        </div>
      ) : (
        <div className="overflow-x-auto border-t" style={{ borderColor: "var(--border-soft)" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg-base)" }}>
              <tr>
                <Th>Stock</Th>
                <Th right>Payouts</Th>
                <Th right>Last paid</Th>
                <Th right>TTM yield</Th>
                <Th right>Yield on cost</Th>
                <Th right>Total earned</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={`${d.symbol}:${d.exchange}`} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`div-cal-row-${d.symbol}`}>
                  <td className="py-4 px-6">
                    <div className="font-medium flex items-center gap-2">
                      {d.symbol}
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: d.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)", color: d.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)" }}>
                        {d.exchange || "NSE"}
                      </span>
                    </div>
                    {d.company && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{d.company}</div>}
                  </td>
                  <Td right mono>{d.count}</Td>
                  <Td right mono>{d.last || "—"}</Td>
                  <Td right mono>{d.ttmYield == null ? "—" : `${d.ttmYield.toFixed(2)}%`}</Td>
                  <Td right mono>{d.yieldOnCost == null ? "—" : `${d.yieldOnCost.toFixed(2)}%`}</Td>
                  <Td right mono className="text-positive">{fmtINR(d.total)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CorporateActionsPanel({ pending, counts, applying, onApply }) {
  if (!pending || pending.length === 0) return null;
  return (
    <section className="surface-card p-5 md:p-7 mb-6 md:mb-8" data-testid="corporate-actions-panel">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div>
          <p className="label-overline mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
            Corporate Actions
          </p>
          <h3 className="font-display text-lg md:text-[21px] font-semibold tracking-[-0.015em]">
            {pending.length} action{pending.length > 1 ? "s" : ""} detected
          </h3>
          <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
            Auto-fetched dividends &amp; splits since you bought each stock · review, then apply to your ledger
          </p>
        </div>
        <button
          data-testid="apply-all-corp-actions"
          onClick={() => onApply(pending)}
          disabled={applying}
          className="inline-flex items-center justify-center gap-2 text-xs md:text-sm px-4 py-2 rounded font-medium transition-colors disabled:opacity-60"
          style={{ background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" }}
        >
          {applying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /> : <Zap className="w-3.5 h-3.5" strokeWidth={2} />}
          Apply all
        </button>
      </div>
      <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-soft)" }}>
        {pending.map((a) => (
          <div key={a.key} className="flex items-center justify-between gap-3 py-2.5" data-testid={`corp-action-${a.key}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <span
                className="shrink-0 w-7 h-7 rounded flex items-center justify-center"
                style={{ background: a.txn_type === "DIVIDEND" ? "var(--tint-accent-bg)" : "var(--tint-neutral-bg)", color: a.txn_type === "DIVIDEND" ? "var(--tint-accent-fg)" : "var(--tint-neutral-fg)" }}
              >
                {a.txn_type === "DIVIDEND" ? <Banknote className="w-4 h-4" strokeWidth={1.75} /> : <Split className="w-4 h-4" strokeWidth={1.75} />}
              </span>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">
                  {a.symbol}
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    {a.txn_type === "DIVIDEND" ? "Dividend" : "Split / Bonus"}
                  </span>
                </div>
                <div className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>
                  {a.date} · {a.description}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-mono text-sm" style={{ color: a.txn_type === "DIVIDEND" ? "var(--accent-positive)" : "var(--text-primary)" }}>
                {a.txn_type === "DIVIDEND" ? (a.currency === "USD" ? fmtUSD(a.amount) : fmtINR(a.amount)) : `×${a.ratio}`}
              </span>
              <button
                data-testid={`apply-corp-action-${a.key}`}
                onClick={() => onApply([a])}
                disabled={applying}
                className="text-xs px-3 py-1.5 rounded border transition-colors hover:bg-white/5 disabled:opacity-60"
                style={{ borderColor: "var(--btn-secondary-border)", color: "var(--text-secondary)" }}
              >
                Apply
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


function MetricCard({ testid, label, value, hint, valueClass = "", trend }) {
  return (
    <div className="surface-card p-4 md:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <p className="label-overline text-[10px] md:text-[0.7rem]">{label}</p>
        {trend === "up" && <ArrowUpRight className="w-4 h-4 shrink-0" style={{ color: "var(--accent-positive)" }} strokeWidth={1.75} />}
        {trend === "down" && <ArrowDownRight className="w-4 h-4 shrink-0" style={{ color: "var(--accent-negative)" }} strokeWidth={1.75} />}
      </div>
      <FitMetric className={`font-metric text-lg md:text-2xl xl:text-[1.7rem] font-light mt-2 md:mt-3 tracking-tight ${valueClass}`}>{value}</FitMetric>
      {hint && <p className="text-[10px] md:text-[11px] mt-1.5 md:mt-2" style={{ color: "var(--text-muted)" }}>{hint}</p>}
    </div>
  );
}

function SubMetric({ testid, label, value, positiveOnly, suffix, hint, money }) {
  const display = value === null || value === undefined
    ? "—"
    : suffix === "%" ? fmtPct(value) : (money ? money(value, { showSign: !positiveOnly }) : fmtINR(value, { showSign: !positiveOnly }));
  const toneClass = value === null || value === undefined || positiveOnly ? "" : colorByValue(value);
  return (
    <div className="surface-card p-4 md:p-6" data-testid={testid}>
      <p className="label-overline text-[10px] md:text-[0.7rem]">{label}</p>
      <FitMetric className={`font-metric text-lg md:text-2xl font-light mt-1.5 md:mt-2 ${toneClass}`}>{display}</FitMetric>
      {hint && <p className="text-[10px] md:text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{hint}</p>}
    </div>
  );
}

// Auto-shrinks a single-line metric value so long ₹ amounts never overflow their
// card. Small values keep their full font size; large ones scale down to fit.
function FitMetric({ children, className = "", style }) {
  const outer = useRef(null);
  const inner = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const measure = () => {
      const o = outer.current, i = inner.current;
      if (!o || !i) return;
      const avail = o.clientWidth;
      const needed = i.scrollWidth;
      setScale(needed > avail && needed > 0 ? Math.max(0.4, avail / needed) : 1);
    };
    measure();
    // Re-measure once brand webfonts finish loading (metrics widths change).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    const ro = new ResizeObserver(measure);
    if (outer.current) ro.observe(outer.current);
    return () => ro.disconnect();
  }, [children]);
  return (
    <div ref={outer} className="w-full overflow-hidden">
      <span
        ref={inner}
        className={`inline-block whitespace-nowrap ${className}`}
        style={{ transform: `scale(${scale})`, transformOrigin: "left center", ...style }}
      >
        {children}
      </span>
    </div>
  );
}

function Th({ children, right, center }) {
  return (
    <th className={`label-overline py-3 px-6 ${center ? "text-center" : right ? "text-right" : "text-left"}`} style={{ color: "var(--text-muted)", fontSize: "0.65rem" }}>
      {children}
    </th>
  );
}
function Td({ children, right, mono, className = "" }) {
  return (
    <td className={`py-4 px-6 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
  );
}

function EmptyChart({ text }) {
  return <div className="h-64 flex items-center justify-center label-overline">{text}</div>;
}

function StalePricesBanner({ oldestAt, lastError, fetching, onRetry, holdingsCount }) {
  if (holdingsCount === 0) return null;

  // Calculate staleness from the oldest price update across active holdings
  const ageMin = oldestAt
    ? Math.floor((new Date().getTime() - new Date(oldestAt).getTime()) / 60000)
    : null;

  // Threshold: 30 min during market hours = stale (Indian market is 09:15–15:30 IST = ~03:45–10:00 UTC)
  const isStale = ageMin !== null && ageMin >= 30;
  const allFailed = lastError?.all === true;
  const networkFail = lastError?.network === true;

  // Only render banner if something is actually wrong
  if (!isStale && !allFailed && !networkFail) return null;

  let title = "Live prices may be out of date";
  let body = "";
  if (networkFail) {
    title = "Live price refresh failed";
    body = `Network error — ${lastError.message}. Tap retry.`;
  } else if (allFailed) {
    title = "Live prices couldn't be fetched";
    body = `Yahoo Finance returned no quotes for any holding (${lastError.count} symbol${lastError.count !== 1 ? "s" : ""}). The provider may be temporarily blocking us. Connecting Upstox below gives reliable real-time NSE/BSE quotes.`;
  } else if (isStale) {
    const human = ageMin < 60 ? `${ageMin}m` : ageMin < 1440 ? `${Math.floor(ageMin / 60)}h ${ageMin % 60}m` : `${Math.floor(ageMin / 1440)}d ${Math.floor((ageMin % 1440) / 60)}h`;
    title = `Prices last refreshed ${human} ago`;
    body = "Day's Gain calculations use the prices stored at that time. Tap refresh to pull current quotes, or connect Upstox for reliable real-time data.";
  }

  return (
    <div
      className="surface-card flex items-start gap-3 p-4 md:p-5 mb-6 md:mb-8 border-l-4"
      style={{
        background: "var(--tint-caution-bg)",
        borderLeftColor: "var(--accent-warning)",
      }}
      data-testid="stale-prices-banner"
    >
      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--accent-warning)" }} strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ color: "var(--tint-caution-fg)" }}>{title}</p>
        <p className="text-[12px] mt-0.5 leading-relaxed" style={{ color: "var(--text-secondary)" }}>{body}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          data-testid="stale-banner-retry"
          onClick={onRetry}
          disabled={fetching}
          className="text-xs font-medium px-3 py-1.5 rounded whitespace-nowrap disabled:opacity-60"
          style={{ background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" }}
        >
          {fetching ? "Refreshing…" : "Refresh now"}
        </button>
        <Link
          to="/prices"
          data-testid="stale-banner-prices"
          className="text-xs font-medium px-3 py-1.5 rounded whitespace-nowrap"
          style={{ border: "1px solid var(--btn-secondary-border)", color: "var(--text-primary)", background: "var(--bg-surface)" }}
        >
          Manage
        </Link>
      </div>
    </div>
  );
}

function HoldingCard({ h, spark }) {
  const m = (v, o) => (h.currency === "USD" ? fmtUSD(v, o) : fmtINR(v, o));
  const exchBadge = {
    background: h.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)",
    color: h.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)",
  };
  return (
    <div className="px-4 py-4" data-testid={`holding-card-${h.symbol}`}>
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="min-w-0">
          <div className="font-medium flex items-center gap-1.5 flex-wrap">
            <span className="truncate">{h.symbol}</span>
            <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={exchBadge}>
              {h.exchange || "NSE"}
            </span>
            {h.market_cap && (
              <span
                className="text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded"
                style={MCAP_BADGE_STYLE[h.market_cap] || MCAP_BADGE_STYLE.SMALL}
              >
                {MCAP_LABEL[h.market_cap] || "Small"}
              </span>
            )}
          </div>
          {h.company_name && (
            <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>
              {h.company_name}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-sm font-medium">{m(h.current_value)}</div>
          <div className={`font-mono text-[11px] ${colorByValue(h.unrealized_gain)}`}>
            {m(h.unrealized_gain, { showSign: true })} · {fmtPct(h.unrealized_pct)}
          </div>
          {h.currency === "USD" && h.current_value_inr != null && (
            <div className="font-mono text-[10px]" style={{ color: "var(--text-muted)" }}>{fmtINR(h.current_value_inr)}</div>
          )}
          <div className="flex justify-end mt-1.5" data-testid={`sparkline-card-${h.symbol}`}>
            <Sparkline data={spark} width={72} height={22} />
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-3 gap-2 text-[11px]">
        <CardCell label="Qty" value={fmtQty(h.quantity)} />
        <CardCell label="Avg" value={m(h.avg_cost, { decimals: 2 })} />
        <CardCell label="LTP" value={h.current_price > 0 ? m(h.current_price) : "—"} />
        <CardCell label="Invested" value={m(h.invested)} />
        <CardCell
          label="Day"
          value={h.previous_close ? `${m(h.day_gain, { showSign: true })}` : "—"}
          sub={h.previous_close ? fmtPct(h.day_gain_pct) : ""}
          tone={h.day_gain}
        />
        <CardCell label="Dividends" value={m(h.dividends)} />
      </dl>
    </div>
  );
}

function CardCell({ label, value, sub, tone }) {
  const toneClass = tone === undefined ? "" : colorByValue(tone);
  return (
    <div>
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-mono mt-0.5 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[9px] font-mono" style={{ color: "var(--text-muted)" }}>{sub}</div>}
    </div>
  );
}

function WhatifPanel({ data }) {
  const actual = data.actual || {};
  const hypo = data.hypothetical || {};
  const delta = data.delta || {};
  const youWin = (actual.current_value || 0) >= (hypo.current_value || 0);
  return (
    <div data-testid="whatif-result">
      {/* Verdict ribbon */}
      <div
        className="rounded-xl px-5 py-4 mb-5 flex items-start gap-3"
        style={{
          background: youWin ? "var(--tint-accent-bg)" : "var(--tint-loss-bg)",
          borderLeft: `3px solid ${youWin ? "var(--accent-positive)" : "var(--accent-negative)"}`,
        }}
      >
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75}
          style={{ color: youWin ? "var(--accent-positive)" : "var(--accent-negative)" }} />
        <div className="text-sm leading-snug">
          {youWin
            ? <>Your picks <b>beat {data.target.name}</b> by {fmtINR(Math.abs(delta.current_value))} &mdash; that&apos;s a <b>+{Math.abs(delta.absolute_return_pct || 0).toFixed(2)}%</b> return edge over the same window.</>
            : <>You&apos;d have been <b>{fmtINR(Math.abs(delta.current_value))} richer</b> in {data.target.name} &mdash; your picks lag by <b>{Math.abs(delta.absolute_return_pct || 0).toFixed(2)}%</b> over the same window.</>
          }
          {data.skipped_buys > 0 && (
            <span className="block text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>
              Note: {data.skipped_buys} later BUY{data.skipped_buys > 1 ? "s" : ""} after day-1 are intentionally excluded so the comparison stays apples-to-apples on the same starting capital.
            </span>
          )}
        </div>
      </div>

      {/* Side-by-side numbers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <WhatifColumn title="Your portfolio" tone="actual" data={actual} />
        <WhatifColumn title={`If you had bought ${data.target.name}`} tone="hypo" data={hypo} />
      </div>

      {/* Growth curves */}
      <WhatifChart actual={actual} hypo={hypo} targetName={data.target.name} />
    </div>
  );
}

function WhatifChart({ actual, hypo, targetName }) {
  const a = actual?.series || [];
  const h = hypo?.series || [];
  if (a.length < 2 && h.length < 2) return null;

  // Merge into a single series keyed by date.
  const byDate = new Map();
  for (const p of a) byDate.set(p.date, { date: p.date, actual: p.value });
  for (const p of h) {
    const row = byDate.get(p.date) || { date: p.date };
    row.hypo = p.value;
    byDate.set(p.date, row);
  }
  const merged = Array.from(byDate.values()).sort((x, y) => x.date.localeCompare(y.date));
  if (merged.length < 2) return null;

  // Forward-fill any sparse points so both lines remain continuous when one
  // side is missing a sample at a given date.
  let lastA = null, lastH = null;
  for (const row of merged) {
    if (row.actual == null && lastA != null) row.actual = lastA;
    else if (row.actual != null) lastA = row.actual;
    if (row.hypo == null && lastH != null) row.hypo = lastH;
    else if (row.hypo != null) lastH = row.hypo;
  }

  const fmtDate = (d) => {
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  };
  const fmtLakh = (v) => {
    if (!isFinite(v)) return "—";
    const a2 = Math.abs(v);
    if (a2 >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (a2 >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
    return `₹${v.toFixed(0)}`;
  };

  const lastActual = [...merged].reverse().find((r) => r.actual != null)?.actual;
  const lastHypo = [...merged].reverse().find((r) => r.hypo != null)?.hypo;
  const winnerActual = (lastActual ?? 0) >= (lastHypo ?? 0);

  return (
    <div className="mt-6">
      <div className="flex items-end justify-between mb-3 gap-2">
        <p className="label-overline">Growth from Day-1</p>
        <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          Weekly close · {merged.length} points
        </p>
      </div>
      <div className="h-72 w-full">
        <ResponsiveContainer>
          <LineChart data={merged} margin={{ top: 10, right: 60, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--border-soft)" strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: "var(--text-muted)" }} minTickGap={32} />
            <YAxis tickFormatter={fmtLakh} tick={{ fontSize: 10, fill: "var(--text-muted)" }} width={62} />
            <Tooltip
              labelFormatter={(d) => fmtDate(d)}
              formatter={(v, n) => [fmtLakh(v), n === "actual" ? "Your portfolio" : targetName]}
              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid var(--border-soft)", background: "var(--bg-surface)", color: "var(--text-primary)" }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
              iconType="line"
              formatter={(name) => (name === "actual" ? "Your portfolio" : targetName)}
            />
            <Line
              type="monotone" dataKey="actual"
              stroke="var(--accent-primary)" strokeWidth={winnerActual ? 2.5 : 2}
              dot={false} activeDot={{ r: 4 }} isAnimationActive={false}
            />
            <Line
              type="monotone" dataKey="hypo"
              stroke="#7E909F" strokeWidth={winnerActual ? 2 : 2.5}
              dot={false} activeDot={{ r: 4 }} strokeDasharray="5 4" isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between text-[11px] mt-1 px-1" style={{ color: "var(--text-muted)" }}>
        <span>Day-1: {fmtLakh(merged[0]?.actual ?? merged[0]?.hypo)}</span>
        <span>
          Today &nbsp;·&nbsp;
          <span style={{ color: "var(--accent-primary)" }}>You {fmtLakh(lastActual)}</span>
          &nbsp;vs&nbsp;
          <span style={{ color: "#7E909F" }}>{targetName} {fmtLakh(lastHypo)}</span>
        </span>
      </div>
    </div>
  );
}

function WhatifColumn({ title, tone, data }) {
  const isActual = tone === "actual";
  const bg = isActual ? "var(--tint-accent-bg)" : "var(--tint-neutral-bg)";
  const accent = isActual ? "var(--accent-primary)" : "#7E909F";
  return (
    <div className="rounded-xl p-5" style={{ background: bg, borderLeft: `2.5px solid ${accent}` }}>
      <p className="label-overline mb-3" style={{ color: accent }}>{title}</p>
      <dl className="space-y-2 text-sm">
        <Row k="Initial Capital · Day 1" v={fmtINR(data.initial_capital)} />
        <Row k="Current value" v={fmtINR(data.current_value)} bold />
        <Row k="Absolute gain" v={fmtINR(data.absolute_gain, { showSign: true })} cls={colorByValue(data.absolute_gain)} />
        <Row k="Return" v={fmtPct(data.absolute_return_pct)} cls={colorByValue(data.absolute_return_pct)} />
        <Row k="CAGR" v={data.xirr_pct == null ? "—" : fmtPct(data.xirr_pct)} cls={colorByValue(data.xirr_pct || 0)} />
      </dl>
    </div>
  );
}

function Row({ k, v, bold, cls }) {
  return (
    <div className="flex items-center justify-between">
      <dt style={{ color: "var(--text-muted)" }}>{k}</dt>
      <dd className={`font-mono ${bold ? "font-semibold" : ""} ${cls || ""}`}>{v}</dd>
    </div>
  );
}

function CorporateActionTile({ action }) {  const cfg = {
    SPLIT: { icon: Split, label: "Stock Split", bg: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)", border: "#7E909F" },
    BONUS: { icon: Gift, label: "Bonus Issue", bg: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)", border: "#7E909F" },
    DIVIDEND: { icon: Banknote, label: "Dividend", bg: "var(--tint-accent-bg)", color: "var(--tint-accent-fg)", border: "var(--accent-primary)" },
  }[action.txn_type] || {};
  const Icon = cfg.icon;
  const detail =
    action.txn_type === "SPLIT"
      ? `1 → ${Number(action.ratio).toFixed(action.ratio % 1 === 0 ? 0 : 2).replace(/\.?0+$/, "")}`
      : action.txn_type === "BONUS"
      ? `+${fmtQty(action.quantity)} shares`
      : (isUSExchange(action.exchange) ? fmtUSD(action.amount) : fmtINR(action.amount));
  const d = new Date(action.date);
  const dateLabel = isNaN(d) ? action.date : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  return (
    <div
      data-testid={`ca-tile-${action.id}`}
      className="shrink-0 rounded-xl px-4 py-3 min-w-[180px] border-l-[3px]"
      style={{ background: cfg.bg, borderLeftColor: cfg.border }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.75} style={{ color: cfg.color }} />}
        <span className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: cfg.color }}>{cfg.label}</span>
      </div>
      <p className="font-medium text-sm leading-tight">{action.symbol}</p>
      <p className="font-mono text-sm mt-1" style={{ color: cfg.color }}>{detail}</p>
      <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>{dateLabel}</p>
    </div>
  );
}

function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded px-3 py-2 text-xs" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-soft)", boxShadow: "0 1px 2px rgba(15,28,40,0.06)" }}>
      <div className="font-medium mb-0.5">{payload[0].name || label}</div>
      <div className="font-mono" style={{ color: "var(--text-secondary)" }}>{formatter ? formatter(payload[0].value) : payload[0].value}</div>
    </div>
  );
}

function timeAgo(date) {
  if (!date) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}
